import type * as React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatDate, formatNumber } from '@/lib/format';

/**
 * Charts for the analytics screens.
 *
 * Recharts, chosen over the alternatives for one reason that matters here: it
 * is declarative React with no imperative canvas lifecycle, so a chart is a
 * component in the same tree as everything else and re-renders on a filter
 * change without an effect reaching into a DOM node. Series colours come from
 * the same `--color-chart-*` tokens as the rest of the panel, so light and dark
 * are one definition rather than two chart themes.
 *
 * ⚠️ Nothing on these screens carries PII. `ANALYST` has no access to personal
 * data at all (06 §1), and the analytics DTOs have no field to put a name in —
 * these components take product ids, ASINs, marketplace codes, counts and
 * dates, and there is no prop here through which a name could arrive.
 */

const AXIS = 'var(--color-ink-faint)';
const GRID = 'var(--color-line)';

function ChartTooltip({
  active,
  payload,
  label,
  labelFormatter,
}: {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number; color?: string }>;
  label?: string | number;
  labelFormatter?: (value: string) => string;
}): React.JSX.Element | null {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="rounded-md border border-line bg-surface px-3 py-2 shadow-lg">
      <p className="text-2xs font-semibold tracking-wide text-ink-faint uppercase">
        {labelFormatter && typeof label === 'string' ? labelFormatter(label) : String(label ?? '')}
      </p>
      <ul className="mt-1 space-y-0.5">
        {payload.map((entry, index) => (
          <li key={index} className="flex items-center gap-2 text-xs text-ink">
            <span
              aria-hidden="true"
              className="size-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-ink-soft">{entry.name}</span>
            <span className="ml-auto font-medium" data-numeric>
              {formatNumber(entry.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface TimeSeriesPoint {
  date: string;
  clicks: number;
}

/** Clicks over time. One series, so no legend — the card title names it. */
export function ClicksOverTime({ data }: { data: TimeSeriesPoint[] }): React.JSX.Element {
  return (
    <ResponsiveContainer width="100%" height={260}>
      <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="clicksFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-chart-1)" stopOpacity={0.28} />
            <stop offset="100%" stopColor="var(--color-chart-1)" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis
          dataKey="date"
          tick={{ fill: AXIS, fontSize: 11 }}
          tickLine={false}
          axisLine={{ stroke: GRID }}
          tickFormatter={(value: string) => formatDate(value)}
          minTickGap={28}
        />
        <YAxis
          tick={{ fill: AXIS, fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          width={48}
          tickFormatter={(value: number) => formatNumber(value)}
        />
        <Tooltip
          content={<ChartTooltip labelFormatter={formatDate} />}
          cursor={{ stroke: GRID, strokeWidth: 1 }}
        />
        <Area
          type="monotone"
          dataKey="clicks"
          name="Clicks"
          stroke="var(--color-chart-1)"
          strokeWidth={2}
          fill="url(#clicksFill)"
          dot={false}
          activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--color-surface)' }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export interface RankedBar {
  label: string;
  value: number;
}

/**
 * Top products. Horizontal, because the labels are product names and a vertical
 * bar chart would rotate them to 45 degrees and make them unreadable. Long names
 * are shortened on the axis; the table below carries them in full.
 */
export function RankedBars({ data }: { data: RankedBar[] }): React.JSX.Element {
  return (
    <ResponsiveContainer width="100%" height={Math.max(200, data.length * 34 + 24)}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 8 }}>
        <CartesianGrid stroke={GRID} horizontal={false} />
        <XAxis
          type="number"
          tick={{ fill: AXIS, fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value: number) => formatNumber(value)}
        />
        <YAxis
          type="category"
          dataKey="label"
          width={190}
          tick={{ fill: AXIS, fontSize: 11 }}
          tickFormatter={(value: string) => shorten(value, 30)}
          tickLine={false}
          axisLine={{ stroke: GRID }}
        />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--color-surface-sunken)' }} />
        <Bar dataKey="value" name="Clicks" radius={[0, 4, 4, 0]} barSize={16}>
          {/* Keyed by position: two products can share a shortened name. */}
          {data.map((entry, index) => (
            <Cell key={`${index}-${entry.label}`} fill="var(--color-chart-1)" />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Cuts a label to `max` characters on a word boundary where there is one. */
function shorten(value: string, max: number): string {
  if (value.length <= max) return value;
  const cut = value.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max / 2 ? cut.slice(0, space) : cut).trimEnd()}…`;
}
